import { useState } from 'react';
import { Button, Grid2 as Grid, Skeleton, Typography } from '@mui/material';
import { BoxedTypography } from 'components/BoxedUrlWithCopy/BoxedUrlWithCopy.styles';
import { ConfirmationDialog } from 'components/Dialogs';
import { useSCIMTokenContext } from 'context';
import {
  REVOKE_ACTION_DIALOG_MESSAGE,
  REVOKE_ACTION_DIALOG_TITLE,
  REVOKE_ACTION_TYPE,
  ROTATE_ACTION_DIALOG_MESSAGE,
  ROTATE_ACTION_DIALOG_TITLE,
  ROTATE_ACTION_TYPE,
} from 'pages/Firms/pages/ScimConfiguration/constants';
import { ActionType, ConfirmationState } from './TokensList.types';

const TokensList = () => {
  const { isLoading, revokeToken, rotateToken, tokens } = useSCIMTokenContext();
  const activeTokens = (tokens ?? []).filter(token => !token.revoked_at);

  const [confirmation, setConfirmation] = useState<ConfirmationState>({
    open: false,
    tokenId: null,
    actionType: null,
  });

  const handleOpenConfirmation = (tokenId: number, actionType: ActionType) => {
    setConfirmation({ open: true, tokenId, actionType });
  };

  const handleCloseConfirmation = () => {
    setConfirmation({ open: false, tokenId: null, actionType: null });
  };

  const handleConfirmAction = () => {
    if (confirmation.tokenId === null) {
      return;
    }

    if (confirmation.actionType === ROTATE_ACTION_TYPE) {
      rotateToken(confirmation.tokenId);
    } else if (confirmation.actionType === REVOKE_ACTION_TYPE) {
      revokeToken(confirmation.tokenId);
    }

    handleCloseConfirmation();
  };

  const getDialogContent = () => {
    if (confirmation.actionType === ROTATE_ACTION_TYPE) {
      return {
        title: ROTATE_ACTION_DIALOG_TITLE,
        message: ROTATE_ACTION_DIALOG_MESSAGE,
        confirmLabel: 'Rotate',
        confirmType: 'success' as const,
      };
    }
    return {
      title: REVOKE_ACTION_DIALOG_TITLE,
      message: REVOKE_ACTION_DIALOG_MESSAGE,
      confirmLabel: 'Revoke',
      confirmType: 'danger' as const,
    };
  };

  if (tokens === null) {
    return <Skeleton variant="rectangular" width="40%" height="100%" />;
  }

  const dialogContent = getDialogContent();

  return (
    <>
      <Grid container direction="column" spacing={2}>
        {activeTokens.map(token => (
          <Grid container key={token.id}>
            <Grid>
              <BoxedTypography variant="body1"> {token.token_prefix}...</BoxedTypography>
              <Typography variant="caption" color="textSecondary">
                Created at: {new Date(token.created_at as string).toLocaleString()}
              </Typography>
            </Grid>
            <Grid>
              <Button
                size="small"
                loading={isLoading}
                onClick={() => handleOpenConfirmation(token.id as number, 'rotate')}>
                Rotate
              </Button>
            </Grid>
            <Grid>
              <Button
                size="small"
                loading={isLoading}
                onClick={() => handleOpenConfirmation(token.id as number, 'revoke')}
                color="error">
                Revoke
              </Button>
            </Grid>
          </Grid>
        ))}
      </Grid>
      <ConfirmationDialog
        open={confirmation.open}
        title={dialogContent.title}
        onClose={handleCloseConfirmation}
        isLoading={isLoading}
        actions={[
          {
            label: 'Cancel',
            variant: 'text',
            callback: handleCloseConfirmation,
          },
          {
            label: dialogContent.confirmLabel,
            variant: 'contained',
            type: dialogContent.confirmType,
            callback: handleConfirmAction,
          },
        ]}>
        <Typography>{dialogContent.message}</Typography>
      </ConfirmationDialog>
    </>
  );
};

export default TokensList;
